import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import * as authApi from '../../api/auth';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, Select } from '../common/Input';
import { Skeleton } from '../common/Skeleton';
import { Alert } from '../common/Alert';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { AuditLogEntry } from '../../types';

const PAGE_SIZE = 50;

interface Filters {
  user_id: string;
  action: string;
  resource_type: string;
  q: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { user_id: '', action: '', resource_type: '', q: '', from: '', to: '' };

function ActionChip({ action }: { action: string }) {
  const verb = action.split('.').pop() ?? action;
  const tone =
    /created|registered|login/.test(verb) ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : /deleted|revoked/.test(verb) ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        : /updated|changed|acknowledged|resolved|imported|redelivered/.test(verb) ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium font-mono ${tone}`}>{action}</span>;
}

/** Render the `changed` diff as "field: from → to" lines, anything else as compact JSON. */
function Details({ entry }: { entry: AuditLogEntry }) {
  const d = entry.details;
  if (!d) return null;
  const changed = d.changed as Record<string, { from: unknown; to: unknown }> | undefined;
  const name = typeof d.name === 'string' ? d.name : null;

  const fmt = (v: unknown) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
  };

  if (changed && Object.keys(changed).length > 0) {
    return (
      <div className="text-xs space-y-0.5">
        {name && <p className="font-medium text-gray-700 dark:text-gray-300">{name}</p>}
        {Object.entries(changed).map(([k, v]) => (
          <p key={k} className="text-gray-600 dark:text-gray-400 break-words">
            <span className="font-mono">{k}</span>: <span className="line-through opacity-70">{fmt(v.from)}</span> → {fmt(v.to)}
          </p>
        ))}
      </div>
    );
  }

  const rest = { ...d };
  delete rest.changed;
  if (name) delete rest.name;
  const summary = JSON.stringify(rest);
  return (
    <div className="text-xs">
      {name && <p className="font-medium text-gray-700 dark:text-gray-300">{name}</p>}
      {summary !== '{}' && (
        <p className="text-gray-500 dark:text-gray-400 font-mono break-all max-w-[28rem]" title={summary}>
          {summary.length > 160 ? `${summary.slice(0, 160)}…` : summary}
        </p>
      )}
    </div>
  );
}

export function AuditLogPage() {
  useDocumentTitle('Audit log');
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [offset, setOffset] = useState(0);

  const params = useMemo(() => ({
    user_id: filters.user_id ? Number(filters.user_id) : undefined,
    action: filters.action || undefined,
    resource_type: filters.resource_type || undefined,
    q: filters.q || undefined,
    from: filters.from || undefined,
    to: filters.to ? `${filters.to}T23:59:59Z` : undefined,
    limit: PAGE_SIZE,
    offset,
  }), [filters, offset]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', params],
    queryFn: () => authApi.getAuditLog(params),
    placeholderData: prev => prev,
  });
  const { data: facets } = useQuery({ queryKey: ['audit-facets'], queryFn: authApi.getAuditFacets });

  function apply(e: React.FormEvent) {
    e.preventDefault();
    setOffset(0);
    setFilters(draft);
  }

  function reset() {
    setDraft(EMPTY);
    setFilters(EMPTY);
    setOffset(0);
  }

  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const update = (k: keyof Filters, v: string) => setDraft(f => ({ ...f, [k]: v }));

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Audit log</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Who changed what, and when. Every sign-in, account change and configuration edit is recorded here; secrets are masked.
        </p>
      </div>

      <Card className="p-4">
        <form onSubmit={apply} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end">
          <div className="lg:col-span-2">
            <Field label="Search">
              {p => <Input {...p} type="search" placeholder="name, id, detail…" value={draft.q} onChange={e => update('q', e.target.value)} />}
            </Field>
          </div>
          <Field label="Action">
            {p => (
              <Select {...p} value={draft.action} onChange={e => update('action', e.target.value)}>
                <option value="">Any</option>
                {facets?.actions.map(a => <option key={a} value={a}>{a}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Type">
            {p => (
              <Select {...p} value={draft.resource_type} onChange={e => update('resource_type', e.target.value)}>
                <option value="">Any</option>
                {facets?.resource_types.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            )}
          </Field>
          <Field label="User">
            {p => (
              <Select {...p} value={draft.user_id} onChange={e => update('user_id', e.target.value)}>
                <option value="">Anyone</option>
                {facets?.users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            )}
          </Field>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1">Filter</Button>
            <Button type="button" variant="secondary" onClick={reset}>Reset</Button>
          </div>
          <Field label="From">
            {p => <Input {...p} type="date" value={draft.from} onChange={e => update('from', e.target.value)} />}
          </Field>
          <Field label="To">
            {p => <Input {...p} type="date" value={draft.to} onChange={e => update('to', e.target.value)} />}
          </Field>
        </form>
      </Card>

      {error && <Alert tone="error">{apiError(error, 'Failed to load the audit log')}</Alert>}

      <Card>
        {isLoading && !data ? (
          <div className="p-4 space-y-3" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !data || data.data.length === 0 ? (
          <p className="p-8 text-center text-gray-500 dark:text-gray-400">No audit entries match.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem]">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  {['When', 'Who', 'Action', 'Resource', 'Details', 'From'].map(h => (
                    <th key={h} scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.data.map(e => (
                  <tr key={e.id} className="align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatDateTime(e.created_at)}</td>
                    <td className="px-4 py-3 text-sm">
                      {e.user_name
                        ? <><span className="text-gray-900 dark:text-gray-100">{e.user_name}</span><span className="block text-xs text-gray-500">{e.user_email}</span></>
                        : <span className="text-gray-500 dark:text-gray-400">{e.user_id === null ? 'system / guest' : `user #${e.user_id}`}</span>}
                    </td>
                    <td className="px-4 py-3"><ActionChip action={e.action} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {e.resource_type ? <>{e.resource_type}{e.resource_id ? <span className="font-mono"> #{e.resource_id}</span> : null}</> : '—'}
                    </td>
                    <td className="px-4 py-3"><Details entry={e} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">{e.ip_address ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
              <span className="self-center text-gray-500 dark:text-gray-400">Page {page} of {pages}</span>
              <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
